import type { ChildKind, ChildReport, LayerReport } from "./rollup";

const SINGULAR: Record<ChildKind, string> = {
  event: "event",
  audience: "audience",
  tile: "tile",
  orphan: "tile not attached to any event",
};

const PLURAL: Record<ChildKind, string> = {
  event: "events",
  audience: "audiences",
  tile: "tiles",
  orphan: "tiles not attached to any event",
};

const CHILD_KIND_ORDER: readonly ChildKind[] = [
  "event",
  "audience",
  "tile",
  "orphan",
];

function countPhrase(kind: ChildKind, count: number): string {
  return `${count} ${count === 1 ? SINGULAR[kind] : PLURAL[kind]}`;
}

function joinPhrases(phrases: string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? "";
  return `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
}

function describeByKind(children: ChildReport[]): string {
  return joinPhrases(
    CHILD_KIND_ORDER.flatMap((kind) => {
      const count = children.filter((child) => child.kind === kind).length;
      return count > 0 ? [countPhrase(kind, count)] : [];
    }),
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function describeFlaggedChild(child: ChildReport): string {
  const detail = child.report ? describeLayerReport(child.report) : undefined;
  const subject = `${capitalize(SINGULAR[child.kind])} ${child.name}`;
  return detail
    ? `${subject} needs attention: ${lowerFirst(detail)}`
    : `${subject} needs attention.`;
}

function describeOnParent(report: LayerReport): string {
  const flagged = report.children.filter(
    (child) => child.status === "NeedsAttention",
  );
  if (flagged.length === 1) {
    const [child] = flagged;
    switch (report.kind) {
      case "dragonTile":
        return `Activated. ${describeFlaggedChild(child)}`;
      case "event":
        return `Event is on. ${describeFlaggedChild(child)}`;
      case "audience":
        return `Audience is on. ${describeFlaggedChild(child)}`;
    }
  }
  if (flagged.length > 1) {
    const phrase = `${describeByKind(flagged)} need attention`;
    switch (report.kind) {
      case "dragonTile":
        return `Activated, but ${phrase}.`;
      case "event":
        return `Event is on, but ${phrase}.`;
      case "audience":
        return `Audience is on, but ${phrase}.`;
    }
  }
  const total = report.children.length;
  switch (report.kind) {
    case "dragonTile":
      if (total === 0) return "Activated, but there is nothing to send yet.";
      if (total === 1) {
        return `Activated, but its only ${SINGULAR[report.children[0].kind]} is off. Nothing will send until it is turned on.`;
      }
      return `Activated, but all ${describeByKind(report.children)} are off. Nothing will send until one is turned on.`;
    case "event":
      return "Event is on, but no emails or texts are activated.";
    case "audience":
      if (total === 0) return "Audience is on, but has no tiles yet.";
      if (total === 1) return "Audience is on, but its only tile is off.";
      return `Audience is on, but none of its ${total} tiles are on.`;
  }
}

function describeFlaggedChildren(flagged: ChildReport[]): string[] {
  if (flagged.length === 0) return [];
  if (flagged.length === 1) return [describeFlaggedChild(flagged[0])];
  return [`${capitalize(describeByKind(flagged))} need attention.`];
}

function describeOffParent(report: LayerReport): string {
  const active = report.children.filter((child) => child.status === "Active");
  const flagged = report.children.filter(
    (child) => child.status === "NeedsAttention",
  );
  const verb = active.length === 1 ? "is" : "are";
  const activeSentence = (waitClause: string) =>
    active.length > 0
      ? [`${capitalize(describeByKind(active))} ${verb} on but ${waitClause}.`]
      : [];
  switch (report.kind) {
    case "dragonTile":
      return [
        "Off.",
        ...activeSentence("will not send until this dragon tile is activated"),
        ...describeFlaggedChildren(flagged),
      ].join(" ");
    case "event":
      return [
        "Event is off.",
        ...activeSentence("will not send while the event is off"),
        ...describeFlaggedChildren(flagged),
      ].join(" ");
    case "audience":
      return `Audience is off, but ${active.length} of its tiles ${verb} on. ${active.length === 1 ? "It" : "They"} will not send until this audience is turned on.`;
  }
}

export function describeLayerReport(report: LayerReport): string | undefined {
  if (report.status !== "NeedsAttention") return undefined;
  return report.ownToggle
    ? describeOnParent(report)
    : describeOffParent(report);
}
